import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
    fetchAllDashboardData,
    fetchDashboardDataIfNeeded,
} from "../../redux/dashboard/dashboardSlice.js";
import Card from "./card";
import {
    DivDashboardCardContainer,
    DivDashboardTableContainer,
    IconDown,
    IconUp,
    SectionContainer,
    SectionTitle,
    DivStartRemediation,
    DivStartRemediationDisabled,
    DivRefreshBar,
    SpanLastFetched,
    ButtonRefresh,
} from "./dashboard.styled";
import {
    baseDetail,
    baseManager,
    baseCustomAttributes,
    baseEmployeeId,
    baseLicense,
    baseDevice,
    baseManagerDemandeur,
    baseOwnerGroup,
    baseGroup,
} from "../../utils/tanstack-table";
import { remediationConfig } from "../../utils/remediationConfig.js";
import { useHasRole } from "../../context/rolesContext.js";
import RemediationPopup from "../others/modal.js";
import Table from "../mainAdvancedSearch/table";

const columnsMap = {
    usersWithoutManager: baseManager,
    usersMissingCustomAttributes: baseCustomAttributes,
    usersWithDuplicateEmployeeId: baseEmployeeId,
    usersWithManagerDisabled: baseManager,
    usersNotInConstellationToutLeMonde: baseDetail,
    usersNotInTeamToutLeMonde: baseDetail,
    usersBALWithManager: baseManager,
    usersBALWithoutSponsors: baseManager,
    usersBALOwnerOfGroup: baseOwnerGroup,
    usersBALWithLicense: baseLicense,
    usersBALWithGroup: baseGroup,
    usersWithoutLicenseIntune: baseLicense,
    usersWithoutLicenseDefender: baseLicense,
    usersManagerWithoutGrpSecManager: baseManager,
    usersManagerWithoutCustomAttribute5: baseManager,
    usersWithMultipleLaptop: baseDevice,
    usersWithoutLaptop: baseDevice,
    usersExternalWithoutDesactivationDate: baseManagerDemandeur,
};

const sections = [
    {
        title: "Users Basic",
        cards: [
            { title: "Users without manager", key: "usersWithoutManager", warning: 10 },
            { title: "Users without custom attributes", key: "usersMissingCustomAttributes", warning: 10 },
            { title: "Users with duplicate employee Id", key: "usersWithDuplicateEmployeeId", warning: 10 },
            { title: "Users manager disabled", key: "usersWithManagerDisabled", warning: 10 },
            { title: "Users not in Constellation tout le monde", key: "usersNotInConstellationToutLeMonde", warning: 10 },
            { title: "Users not in Team tout le monde", key: "usersNotInTeamToutLeMonde", warning: 10 },
            { title: "Users without license Intune", key: "usersWithoutLicenseIntune", warning: 10 },
            { title: "Users without license Defender", key: "usersWithoutLicenseDefender", warning: 10 },
            { title: "Users with multiple laptop", key: "usersWithMultipleLaptop", warning: 10 },
            { title: "Users without laptop", key: "usersWithoutLaptop", warning: 10 },
        ],
    },
    {
        title: "Users BAL",
        cards: [
            { title: "Users BAL with manager", key: "usersBALWithManager", warning: 10 },
            { title: "Users BAL without sponsors", key: "usersBALWithoutSponsors", warning: 10 },
            { title: "Users BAL owner of group", key: "usersBALOwnerOfGroup", warning: 10 },
            { title: "Users BAL with license", key: "usersBALWithLicense", warning: 10 },
            { title: "Users BAL with group", key: "usersBALWithGroup", warning: 10 },
        ],
    },
    {
        title: "Users Manager",
        cards: [
            { title: "Users manager without GrpSecManager", key: "usersManagerWithoutGrpSecManager", warning: 10 },
            { title: "Users manager without Custom 5", key: "usersManagerWithoutCustomAttribute5", warning: 10 },
        ],
    },
    {
        title: "Users Presta",
        cards: [
            { title: "Users presta without desactivation date", key: "usersExternalWithoutDesactivationDate", warning: 10 },
        ],
    },
];

const colVisibility = {
    ID: false,
    streetAddress: false,
    postalCode: false,
    mobilePhone: false,
    givenName: false,
    surname: false,
    accountEnabled: false,
};

export default function Dashboard() {
    const dispatch = useDispatch();
    const { data, isLoading, lastFetched } = useSelector((state) => state.dashboard);

    const canRemediate = useHasRole(["Users.Admin"]);
    const [selectedRow, setSelectedRow] = useState({});
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [activeTable, setActiveTable] = useState({ key: null, title: null });
    const [openSection, setOpenSection] = useState({
        "Users Basic": false,
        "Users BAL": false,
        "Users Manager": false,
        "Users Presta": false,
    });

    const isFetching = useMemo(
        () => Object.values(isLoading).some(Boolean),
        [isLoading],
    );

    const tableData = activeTable.key ? data[activeTable.key] : [];
    const tableColumns = activeTable.key
        ? columnsMap[activeTable.key] || baseManager
        : baseManager;

    useEffect(() => {
        dispatch(fetchDashboardDataIfNeeded());
    }, [dispatch]);

    const handleRefresh = () => {
        setSelectedRow({});
        dispatch(fetchAllDashboardData());
    };

    const onRowSelectionChange = useCallback((row) => {
        setSelectedRow(row);
    }, []);

    const openPopup = () => setIsPopupOpen(true);
    const closePopup = () => setIsPopupOpen(false);

    const handleCardClick = (title, key) => {
        setSelectedRow({});
        if (activeTable.key !== key) {
            setActiveTable({ key, title });
        } else {
            setActiveTable({ key: null, title: null });
        }
    };

    const toggleSection = (sectionTitle) => {
        setOpenSection((prev) => ({
            ...prev,
            [sectionTitle]: !prev[sectionTitle],
        }));
    };

    return (
        <>
            {/* ---- Bouton Refresh ---- */}
            <DivRefreshBar>
                {lastFetched && (
                    <SpanLastFetched>
                        Dernière mise à jour : {new Date(lastFetched).toLocaleString()}
                    </SpanLastFetched>
                )}
                <ButtonRefresh onClick={handleRefresh} disabled={isFetching}>
                    {isFetching ? "Chargement…" : "🔄 Rafraîchir"}
                </ButtonRefresh>
            </DivRefreshBar>

            {/* ---- Sections & Cards ---- */}
            {sections.map((section) => (
                <SectionContainer key={section.title}>
                    <SectionTitle>
                        <h5>{section.title}</h5>
                        {openSection[section.title] ? (
                            <IconUp
                                className="bx bx-chevrons-up"
                                onClick={() => toggleSection(section.title)}
                            />
                        ) : (
                            <IconDown
                                className="bx bx-chevrons-down"
                                onClick={() => toggleSection(section.title)}
                            />
                        )}
                    </SectionTitle>
                    <DivDashboardCardContainer
                        className={openSection[section.title] ? "active" : "inactive"}
                    >
                        {section.cards.map(({ title, key, warning }) => (
                            <Card
                                key={key}
                                title={title}
                                dataCount={data[key]?.length || 0}
                                isLoading={isLoading[key]}
                                onClick={() => handleCardClick(title, key)}
                                warning={warning}
                            />
                        ))}
                    </DivDashboardCardContainer>
                </SectionContainer>
            ))}

            {/* ---- Tableau ---- */}
            {activeTable.key && (
                <>
                    <DivDashboardTableContainer>
                        <span>{activeTable.title}</span>
                        <Table
                            key={activeTable.key}
                            columns={tableColumns}
                            data={tableData}
                            colVisibility={colVisibility}
                            columnsType="Users"
                            onRowSelectionChange={onRowSelectionChange}
                        />
                    </DivDashboardTableContainer>

                    {canRemediate ? (
                        <div style={{ display: "flex", justifyContent: "center" }}>
                            <DivStartRemediation onClick={openPopup}>
                                <span>Start remediation</span>
                            </DivStartRemediation>
                        </div>
                    ) : (
                        <div style={{ display: "flex", justifyContent: "center" }}>
                            <DivStartRemediationDisabled>
                                <span>Start remediation 🔒</span>
                            </DivStartRemediationDisabled>
                        </div>
                    )}

                    {isPopupOpen && canRemediate && (
                        <RemediationPopup
                            onClose={closePopup}
                            description={remediationConfig[activeTable.key].description}
                            selectedRow={selectedRow}
                            remediationFunction={remediationConfig[activeTable.key].remediationFunction}
                            formatResultMessage={remediationConfig[activeTable.key].formatResultMessage}
                            buttonLabel={remediationConfig[activeTable.key].buttonLabel}
                        />
                    )}
                </>
            )}
        </>
    );
}